import { exportToBlob } from "@jitsi/excalidraw";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTools } from "../hooks/useTools";
import { useWhiteboardPaste } from "../hooks/useWhiteboardPaste";
import { getClipboardItemsFromNavigator, getClipboardItemsFromPasteEvent } from "../utils/paste";
import { ToolbarButton } from "./ToolbarButton";
import { IconMousePointer, IconHand, IconPencil, IconCircunference, IconRectangle, IconEraser, IconText, IconClipboard, IconImportImage, IconExportImage, IconUndo, IconRedo, IconArrowRightDown } from "../../../base/icons/svg";
import { shareBlobImage } from "../../../collab-images/actions";
import { showErrorNotification, showSuccessNotification, showWarningNotification } from "../../../notifications/actions";
import { NOTIFICATION_TIMEOUT_TYPE } from "../../../notifications/constants";
import { getExportButton } from "../utils/canvas";
import React from "react";
import ToolPanel from "./ToolPanel";
import { useToolPanel } from "../hooks/useToolPanel";
import "../styles/toolbar.css";
export default function Toolbar({ excalidrawApi, patientFileUploader }) {
    const { addToExcalidraw } = useWhiteboardPaste(excalidrawApi);
    const { currentTool, setTool } = useTools(excalidrawApi);
    const [show, setShow] = useState(true);
    const dispatch = useDispatch();
    const toolPanel = useToolPanel(excalidrawApi);
    function handleSetTool(newTool) {
        if (currentTool !== newTool) {
            setShow(true);
            setTool(newTool);
        }
        else {
            setShow(!show);
        }
    }
    useEffect(() => {
        const pasteHandler = async (e) => {
            e.stopImmediatePropagation();
            e.preventDefault();
            const item = (await getClipboardItemsFromPasteEvent(e))[0];
            if (!item)
                return;
            addToExcalidraw(item);
        };
        document.addEventListener('paste', pasteHandler);
        return () => {
            document.removeEventListener('paste', pasteHandler);
        };
    }, [addToExcalidraw]);
    return (React.createElement("div", { className: "cwb__toolbar" },
        React.createElement(ToolbarButton, { isActive: currentTool == 'selection', onClick: () => setTool('selection'), title: "Seleccionar" },
            React.createElement(IconMousePointer, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'pan', onClick: () => setTool('pan'), title: "Arrastrar vista" },
            React.createElement(IconHand, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'freedraw', onClick: () => handleSetTool('freedraw'), title: "Dibujar", panel: {
                show: show && currentTool == "freedraw",
                content: React.createElement(ToolPanel, { toolPanel: toolPanel, config: { color: true, stroke: true } })
            } },
            React.createElement(IconPencil, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'ellipse', onClick: () => handleSetTool('ellipse'), title: "Elipse", panel: {
                show: show && currentTool == "ellipse",
                content: React.createElement(ToolPanel, { toolPanel: toolPanel, config: { color: true, stroke: true } })
            } },
            React.createElement(IconCircunference, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'rectangle', onClick: () => handleSetTool('rectangle'), title: "Rectangle", panel: {
                show: show && currentTool == "rectangle",
                content: React.createElement(ToolPanel, { toolPanel: toolPanel, config: { color: true, stroke: true } })
            } },
            React.createElement(IconRectangle, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'arrow', onClick: () => handleSetTool('arrow'), title: "Flecha", panel: {
                show: show && currentTool == "arrow",
                content: React.createElement(ToolPanel, { toolPanel: toolPanel, config: { color: true, stroke: true } })
            } },
            React.createElement(IconArrowRightDown, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'eraser', onClick: () => setTool('eraser'), title: "Borrador" },
            React.createElement(IconEraser, null)),
        React.createElement(ToolbarButton, { isActive: currentTool == 'text', onClick: () => handleSetTool('text'), title: "Texto", panel: {
                show: show && currentTool == "text",
                content: React.createElement(ToolPanel, { toolPanel: toolPanel, config: { color: true, fontSize: true } })
            } },
            React.createElement(IconText, null)),
        React.createElement("div", { className: "cwb_toolbar_separator" }),
        React.createElement(ToolbarButton, { onClick: async () => {
                try {
                    const item = (await getClipboardItemsFromNavigator())[0];
                    if (!item) {
                        dispatch(showWarningNotification({
                            title: "Portapapeles",
                            description: 'No hay elementos en el portapapeles.',
                            hideErrorSupportLink: true
                        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                        return;
                    }
                    addToExcalidraw(item);
                }
                catch (e) {
                    dispatch(showWarningNotification({
                        title: "Portapapeles",
                        description: 'No se ha podido acceder al portapapeles',
                        hideErrorSupportLink: true
                    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                }
            }, title: "Pegar imagen (Ctrl+V)" },
            React.createElement(IconClipboard, null)),
        React.createElement(ToolbarButton, { onClick: () => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.style.display = 'none';
                input.onchange = () => {
                    if (input.files && input.files.length > 0) {
                        const file = input.files[0];
                        // @ts-ignore
                        const blob = new Blob([file], { type: file.type });
                        dispatch(shareBlobImage(blob, excalidrawApi));
                    }
                };
                document.body.appendChild(input);
                input.click();
                document.body.removeChild(input);
            }, title: "Importar imagen" },
            React.createElement(IconImportImage, null)),
        React.createElement(ToolbarButton, { onClick: () => {
                function waitForModal(selector, interval = 100, timeout = 5000) {
                    return new Promise((resolve, reject) => {
                        const start = Date.now();
                        const timer = setInterval(() => {
                            const el = document.querySelector(selector);
                            if (el) {
                                clearInterval(timer);
                                resolve(el);
                            }
                            else if (Date.now() - start > timeout) {
                                clearInterval(timer);
                                reject(new Error('Modal no apareció a tiempo'));
                            }
                        }, interval);
                    });
                }
                const exportButton = getExportButton();
                if (!exportButton)
                    return;
                exportButton.click();
                waitForModal('.excalidraw.excalidraw-modal-container', 65)
                    .then((modal) => {
                    const inputElement = modal.querySelector("input");
                    if (inputElement && patientFileUploader) {
                        inputElement.value = patientFileUploader.getName();
                    }
                    const buttonsContainer = modal.querySelector(".Stack.Stack_horizontal");
                    if (buttonsContainer) {
                        const newButton = document.createElement("button");
                        newButton.textContent = "Subir a ficha clínica";
                        newButton.classList.add("excalidraw-button", "test-button");
                        newButton.type = "button";
                        newButton.style.cssText = `
                                margin-left: 8px;
                                background: #007bff;
                                color: white;
                                border-radius: 1rem;
                                padding: 4px 10px;
                                cursor: pointer;
                                box-shadow: 
                                    0 3px 5px -1px rgba(0,0,0,0.28), 
                                    0 6px 10px 0 rgba(0,0,0,0.14);
                                `;
                        newButton.addEventListener("click", async () => {
                            if (!patientFileUploader) {
                                dispatch(showErrorNotification({
                                    title: "Error",
                                    description: "Esta caracteristica no se encuentra activa.",
                                    hideErrorSupportLink: true
                                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                                return;
                            }
                            const state = excalidrawApi.getAppState();
                            const files = excalidrawApi.getFiles();
                            const elements = excalidrawApi.getSceneElements();
                            let selectedElements = elements.filter(el => state.selectedElementIds[el.id]);
                            if (selectedElements.length === 0) {
                                selectedElements = [...elements];
                            }
                            const blob = await exportToBlob({
                                elements: selectedElements,
                                files,
                                quality: 1
                            });
                            const value = modal.querySelector('input')?.value;
                            const filename = `${value || patientFileUploader.getName()}.png`;
                            try {
                                await patientFileUploader.handle(blob, 7, filename);
                                dispatch(showSuccessNotification({
                                    title: "Exito",
                                    description: 'La imagen fue subida a la ficha clinica'
                                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                            }
                            catch (error) {
                                dispatch(showErrorNotification({
                                    title: "Error",
                                    description: "Ha ocurrido un error inesperado.",
                                    hideErrorSupportLink: true
                                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                            }
                        });
                        buttonsContainer.appendChild(newButton);
                    }
                    else {
                        console.warn("⚠️ No se encontró el contenedor de botones dentro del modal");
                    }
                })
                    .catch(err => console.error(err.message));
            }, title: "Exportar imagen" },
            React.createElement(IconExportImage, null)),
        React.createElement("div", { className: "cwb_toolbar_separator" }),
        React.createElement(ToolbarButton, { onClick: () => {
                const targets = document.querySelectorAll(".excalidraw .undo-redo-buttons button");
                const target = targets[0];
                if (!target)
                    return;
                target.click();
            }, title: "Deshacer (Ctrl+Z)" },
            React.createElement(IconUndo, null)),
        React.createElement(ToolbarButton, { onClick: () => {
                const targets = document.querySelectorAll(".excalidraw .undo-redo-buttons button");
                const target = targets[1];
                if (!target)
                    return;
                target.click();
            }, title: "Rehacer (Ctrl+Y)" },
            React.createElement(IconRedo, null))));
}
